import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../../components/PageHero';
import {
  IconClipboard, IconWind, IconShield, IconTool, IconTarget,
  IconCheckCircle, IconFileText,
  IconBarChart, IconCpu, IconActivity,
} from '../../components/Icons';
import '../nosotros/Nosotros.css';
import './Software.css';

const MODULES = [
  {
    Icon: IconClipboard,
    color: 'green',
    title: 'Planificación Minera',
    items: [
      'Selección del método de explotación',
      'Diseño de voladuras: geometría, carga y fragmentación',
      'Economía minera: VPN, TIR y flujo de caja',
      'Análisis de sensibilidad con simulación Monte Carlo',
    ],
  },
  {
    Icon: IconWind,
    color: 'blue',
    title: 'Ventilación y Control de Gases',
    items: [
      'Redes de ventilación principal y auxiliar',
      'Monitoreo continuo de gases (CO, CH₄, CO₂, O₂)',
      'Dimensionamiento de ventiladores y ductos',
      'Control de polvo respirable y cumplimiento TLV',
    ],
  },
  {
    Icon: IconShield,
    color: 'green',
    title: 'Sostenimiento Subterráneo',
    items: [
      'Diseño de pernos y cables de anclaje',
      'Arcos de shotcrete: espesor y resistencia',
      'Estructuras de madera alemana y canastas',
      'Protocolos de inspección y mantenimiento',
    ],
  },
  {
    Icon: IconTool,
    color: 'blue',
    title: 'Gestión de Equipos',
    items: [
      'Bombas de drenaje: caudal, presión y potencia',
      'Compresores: caudal y ciclo de trabajo',
      'Sistemas eléctricos: transformadores y cables',
      'Malacoates y sistemas de izaje',
    ],
  },
  {
    Icon: IconBarChart,
    color: 'blue',
    title: 'Reportes y Documentación',
    items: [
      'Exportación automática a Excel, PDF y Word',
      'Gráficas técnicas de publicación',
      'Informes para ANM, SGC y entes de control',
      'Trazabilidad de cálculos con base de datos cifrada',
    ],
  },
  {
    Icon: IconActivity,
    color: 'green',
    title: 'Monitoreo en Tiempo Real',
    items: [
      'Dashboard integrado de indicadores operativos',
      'Alertas y semáforos de cumplimiento normativo',
      'Tendencias históricas y análisis predictivo',
      'Registro de eventos con auditoría de usuario',
    ],
  },
];

const HIGHLIGHTS = [
  {
    Icon: IconCpu,
    color: 'green',
    title: 'Plataforma integrada',
    desc: 'Un solo sistema cubre minería, geología, geomecánica y SST sin necesidad de software adicional.',
  },
  {
    Icon: IconShield,
    color: 'blue',
    title: 'Seguridad avanzada',
    desc: 'Base de datos cifrada con SQLCipher, autenticación biométrica y control de acceso por roles.',
  },
  {
    Icon: IconFileText,
    color: 'green',
    title: 'Cumplimiento normativo',
    desc: 'Alineado con Decreto 1886/2015, Decreto 944/2022, Ley 685/2001, Res. 0357/2002, Res. 0090/2006 y estándares JORC/NI 43-101.',
  },
  {
    Icon: IconTarget,
    color: 'blue',
    title: 'Rigor científico',
    desc: 'Cálculos basados en metodologías peer-reviewed: Hoek-Brown, Barton Q-System, Bieniawski RMR.',
  },
];


export default function SoftwareMinero() {
  return (
    <div>
      <PageHero
        title="Software Minero"
        subtitle="Sistema integral de ingeniería minera: planificación, geomecánica, ventilación, recursos y seguridad en una sola plataforma."
        variant="green"
      />

      {/* ── Overview ── */}
      <section className="section">
        <div className="container">
          <div className="qs-grid">
            <div className="qs-text anim-fade-up">
              <div className="divider" />
              <h2 className="section-title">Sistema Integral de Ingeniería Minera</h2>
              <p>
                Nuestro software minero es una plataforma de escritorio de nivel profesional,
                desarrollada internamente por <strong>Impacta M.A. SAS</strong> para dar respuesta
                a las necesidades técnicas y normativas del sector minero colombiano. Integra en un
                único entorno todos los procesos de ingeniería que requiere una operación minera
                moderna: desde la planificación del método de explotación hasta el control de
                ventilación, la caracterización geomecánica y la gestión de seguridad industrial.
              </p>
              <p>
                Cada módulo incorpora las metodologías de referencia internacional —Hoek-Brown,
                Barton Q-System, RMR Bieniawski, CRIRSCO, JORC— adaptadas a la normativa minera
                colombiana vigente: <strong>Decreto 1886/2015</strong> (seguridad en labores
                subterráneas), <strong>Decreto 944/2022</strong> (actualización de seguridad minera),
                <strong> Ley 685/2001</strong> (Código de Minas), <strong>Resolución 0357/2002</strong>,{' '}
                <strong>Resolución 0090/2006</strong>, <strong>Resolución 40103/2017</strong> (RETIE),
                y las resoluciones vigentes de la <strong>Agencia Nacional Minera (ANM)</strong>.
              </p>
              <p>
                La plataforma cuenta con base de datos cifrada (SQLCipher), autenticación biométrica
                facial, trazabilidad de cálculos y generación automática de reportes técnicos en
                Excel, PDF y Word, listos para presentación ante autoridades mineras.
              </p>
            </div>
            <div className="qs-card anim-fade-up anim-delay-2">
              <div className="qs-card__logo-wrap">
                <picture>
                  <source srcSet="/logo3.webp" type="image/webp" />
                  <img src="/logo3.webp" alt="Impacta M.A. SAS" />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Modules ── */}
      <section className="section section--bg">
        <div className="container">
          <div className="divider" />
          <h2 className="section-title">Módulos del Sistema</h2>
          <p className="section-lead" style={{ marginBottom: '40px' }}>
            Cada módulo ejecuta los cálculos técnicos, gestiona la información y genera el reporte
            correspondiente de forma automática.
          </p>
          <div className="sw-modules-grid">
            {MODULES.map(({ Icon, color, title, items }) => (
              <div key={title} className="sw-module-card">
                <div className={`sw-module-card__icon sw-module-card__icon--${color}`}>
                  <Icon size={26} />
                </div>
                <h3>{title}</h3>
                <ul>
                  {items.map(item => (
                    <li key={item}>
                      <IconCheckCircle
                        size={14}
                        className="sw-check"
                        style={{ color: color === 'green' ? 'var(--c-green-500)' : 'var(--c-blue-500)' }}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Highlights ── */}
      <section className="section">
        <div className="container">
          <div className="divider divider--center" />
          <h2 className="section-title section-title--center">¿Por qué usar nuestro software?</h2>
          <div className="sw-highlights" style={{ marginTop: '40px' }}>
            {HIGHLIGHTS.map(({ Icon, color, title, desc }) => (
              <div key={title} className="sw-highlight card card--elevated">
                <div className={`sw-highlight__icon sw-highlight__icon--${color}`}>
                  <Icon size={26} />
                </div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section section--dark">
        <div className="container" style={{ textAlign: 'center' }}>
          <div className="divider divider--center" style={{ background: 'var(--c-green-400)' }} />
          <h2 className="section-title section-title--white" style={{ marginBottom: '12px' }}>
            ¿Desea conocer el software?
          </h2>
          <p className="section-lead section-lead--center section-lead--white" style={{ marginBottom: '32px' }}>
            Contáctenos para una demostración personalizada del sistema o para cotizar su implementación en su operación minera.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contacto" className="btn btn--primary btn--lg">Solicitar Demostración</Link>
            <Link to="/software/ambiental" className="btn btn--ghost-white btn--lg">Ver Software Ambiental</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
